import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { useChangeRequestDetail } from "~/features/changeRequests/hooks";
import { toChangeRequestDetailUI } from "~/features/changeRequests/utils/transformers";
import { ChangeRequestDetailPresenter } from "./ChangeRequestDetailPresenter";

interface ChangeRequestDetailProps {
  id: string;
  onBack: () => void;
  variant?: "page" | "peek";
}

export function ChangeRequestDetail({ id, onBack, variant = "page" }: ChangeRequestDetailProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { data } = useChangeRequestDetail(id);

  const raw = data.changeRequests[0];
  const detail = useMemo(() => (raw ? toChangeRequestDetailUI(raw, locale) : null), [raw, locale]);

  const onCopy = useCallback(
    async (value: string) => {
      try {
        await copyToClipboard(value);
        toast.success(t("feature.common.idCopied"));
      } catch {
        toast.error(t("feature.common.copyToClipboardFailed"));
      }
    },
    [t],
  );

  if (!raw || !detail) {
    return <div className="p-8 text-14-regular text-fg-3">{t("ui.notFound")}</div>;
  }

  return <ChangeRequestDetailPresenter detail={detail} variant={variant} onBack={onBack} onCopy={onCopy} />;
}
